import React from 'react';
import PropTypes from 'prop-types';
import { path } from 'ramda';

import { mainHeader, subHeader, subjectArea } from './Info.less';

import { mapFSMCTotalToFatigueLevel, mapFSMCPhysicalToFatigueLevel, mapFSMCMentalToFatigueLevel } from 'neuro-calculation-commons';

const fatigueLevelLocalizationsEN = {
  none: 'Based on your self-rating, you do not perceive fatigue',
  mild: 'Based on your self-rating, you perceive mild fatigue',
  moderate: 'Based on your self-rating, you perceive moderate fatigue',
  severe: 'Based on your self-rating, you perceive severe fatigue',
};

const physicalFatigueLevelLocalizationsEN = {
  none: 'Based on your self-rating, you do not perceive physical fatigue',
  mild: 'Based on your self-rating, you perceive mild physical fatigue',
  moderate: 'Based on your self-rating, you perceive moderate physical fatigue',
  severe: 'Based on your self-rating, you perceive severe physical fatigue',
};

const cognitiveFatigueLevelLocalizationsEN = {
  none: 'Based on your self-rating, you do not perceive cognitive fatigue',
  mild: 'Based on your self-rating, you perceive mild cognitive fatigue',
  moderate: 'Based on your self-rating, you perceive moderate cognitive fatigue',
  severe: 'Based on your self-rating, you perceive severe cognitive fatigue',
};

const InfoEN = ({ totalScore, statusMood, document }) => (
  <React.Fragment>
    <div className={mainHeader}>
      Feedback on the self-perceived fatigue based on the Fatigue Scale for Motor and Cognitive Functions
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Total score</div>
      <ul>
        <li>
          Your score was <span style={{ fontWeight: 600 }}>{totalScore}</span> points (min 0, max 100; higher score
          refers to more prominent self-perceived fatigue).
        </li>
        {<li>{fatigueLevelLocalizationsEN[mapFSMCTotalToFatigueLevel(totalScore)]}</li>}
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Physical score</div>
      <ul>
        <li>
          Your score on the physical scale was{' '}
          <span style={{ fontWeight: 600 }}>{path(['scores', 'physical'], document)}</span> (min 0, max 50; higher score
          refers to more prominent self-perceived fatigue).
        </li>
        {<li>{physicalFatigueLevelLocalizationsEN[mapFSMCPhysicalToFatigueLevel(path(['scores', 'physical'], document))]}</li>}
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Cognitive score</div>
      <ul>
        <li>
          Your score on the cognitive scale was{' '}
          <span style={{ fontWeight: 600 }}>{path(['scores', 'mental'], document)}</span> points (min 0, max 50; higher
          score refers to more prominent self-perceived fatigue).
        </li>
        {<li>{cognitiveFatigueLevelLocalizationsEN[mapFSMCMentalToFatigueLevel(path(['scores', 'mental'], document))]}</li>}
      </ul>
    </div>

    {statusMood > 0 && (
      <div className={subjectArea}>
        <span>
          It is important to notice that your self-perceived fatigue symptoms may be at least partly explained by
          self-perceived problems in mood. It is important to discuss these problems with a specialized nurse or your
          treating neurologist.
        </span>
      </div>
    )}

    <div className={subjectArea}>
      <div className={subHeader}>Information on fatigue</div>
      Fatigue may affect your daily life in many ways. You may come along with fatigue for long periods of time but
      there may also be periods when fatigue feels like a symptom which is extremely hard to manage. It is easier to
      come along with fatigue when you are aware of the times when it typically occurs, as well as of the factors which
      ameliorate or deteriorate the symptom. Based on the questionnaire, you might have got information on whether your
      fatigue is manifested physically and / or cognitively. You will have even more information on your fatigue if you
      keep diary for example for a couple of weeks. In the diary, you can make notes on sleeping and eating as well as
      physical, social, and cognitive activities and the effects of those on your feeling of fatigue. You can make the
      notes for example each hour together with your evaluation on the degree of your fatigue by using the scale from 0
      to 10 where 0 stands for no fatigue and 10 for total fatigue. By examining your notes, you may recognize whether
      sleep, nutrition, or activities affect your well-being and vitality.
      <br />
      <br />
      The following tips may help you to come along with your feelings of fatigue:
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Useful tips</div>
      Sleep
      <br />
      <ul>
        <li>Pay attention to the sleep hygiene</li>
        <ul>
          <li>try to appease a while before you go to sleep</li>
          <li>make a list on the duties for the next day before you go to sleep</li>
          <li>eliminate factors that may disturb your sleep</li>
          <li>find help for pain or incontinency problems if they disturb your sleep</li>
        </ul>
      </ul>
      <br />
      Nutrition
      <ul>
        <li>pay attention to healthy diet</li>
        <ul>
          <li>it is important to try to keep energy level stable to avoid fatigue</li>
          <li>try to eat breakfast, lunch, snack, dinner, and evening snack</li>
          <li>try to avoid too long periods without eating</li>
          <li>eating smaller meals with more vegetables may help in controlling for fatigue</li>
          <li>remember to drink enough</li>
        </ul>
      </ul>
      <br />
      Physical and cognitive activity, and energy conservation
      <ul>
        <li>take care of your physical condition</li>
        <ul>
          <li>stay physically active without fatiguing yourself excessively</li>
          <li>tailor-made physical exercising often reduces feelings of fatigue</li>
        </ul>
        <li>take care of your cognitive abilities</li>
        <ul>
          <li>
            meaningful activities promote the functioning of neural networks, protect from cognitive deterioration, and
            support resilience
          </li>
        </ul>
        <li>pay attention to when you are performing, how you are performing, and what you are performing</li>
        <ul>
          <li>use to-do-lists</li>
          <li>do the most important things when you are most alert</li>
          <li>avoid hustle and multitasking</li>
          <li>take enough time</li>
          <li>utilise energy conservation</li>
          <ul>
            <li>use aids</li>
            <li>eliminate distractors</li>
            <li>try to be aware of your energy level – make systematic breaks to avoid fatigue</li>
          </ul>
        </ul>
      </ul>
      <br />
      Distractors
      <ul>
        <li>if your performance is easily distracted, try to eliminate systematic and redundant distractors</li>
        <li>if you are heat sensitive</li>
        <ul>
          <li>avoid sauna or use cooling during sauna or bathing</li>
          <li>try to drop the room temperature</li>
          <li>test different cooling instruments – ice packs, cooling wests and helmets, cold showers etc.</li>
        </ul>
      </ul>
    </div>
    <div className={subjectArea}>
      <div className={subHeader}>And next...</div>
      It is important that you stay active despite the feelings of fatigue. If fatigue affects your daily life, or
      ability to work or study, discuss with your MS-nurse or neurologist on further steps. You can find more
      information from{' '}
      <a href="https://www.neuroliitto.fi" target="blank">
        www.neuroliitto.fi
      </a>{' '}
      (information on the Finnish Neuro Society),{' '}
      <a href="https://www.kela.fi" target="blank">
        www.kela.fi
      </a>{' '}
      (information on rehabilitation options), and{' '}
      <a href="https://www.appeco.net" target="blank">
        www.appeco.net
      </a>{' '}
      (information on the effects of rehabilitation).
    </div>
  </React.Fragment>
);

InfoEN.propTypes = {
  totalScore: PropTypes.number,
  statusMood: PropTypes.number,
  document: PropTypes.object
};

export default InfoEN;
